import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCheckCircle,
  faSync,
  faExclamationTriangle,
  faTimes as faTimesCircle,
  faFileExcel,
  faClock,
  faChartBar,
  faChevronDown,
  faChevronUp,
  faDownload,
  faEye,
  faInfoCircle,
  faFileUpload,
  faDatabase,
  faSpinner,
  faCog,
  faChartPie,
} from "@fortawesome/free-solid-svg-icons";

const UploadResultsModal = ({ isOpen, onClose, uploadResult }) => {
  const [expandedSections, setExpandedSections] = useState({
    insertedRecords: false,
    updatedRecords: false,
    skippedRecords: false,
    failedRecords: false,
    validationIssues: false,
    dataQuality: false,
  });

  if (!isOpen || !uploadResult) return null;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getSuccessRate = () => {
    return uploadResult.summary?.successRate || 0;
  };

  const getSuccessRateColor = () => {
    const rate = getSuccessRate();
    if (rate >= 90) return "text-green-600";
    if (rate >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  const StatCard = ({ icon, title, value, color = "blue", subtitle }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center">
        <div className={`p-3 rounded-full bg-${color}-100 mr-4`}>
          <FontAwesomeIcon icon={icon} className={`text-${color}-600 text-xl`} />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const ProgressBar = ({ percentage, color = "blue" }) => (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className={`bg-${color}-600 h-2.5 rounded-full transition-all duration-300`}
        style={{ width: `${percentage}%` }}
      ></div>
    </div>
  );

  const ExpandableSection = ({ title, count, icon, color, children, sectionKey, badge }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
      >
        <div className="flex items-center">
          <FontAwesomeIcon icon={icon} className={`text-${color}-600 mr-3`} />
          <span className="font-medium text-gray-900">{title}</span>
          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`}>
            {count}
          </span>
          {badge && (
            <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {badge}
            </span>
          )}
        </div>
        <FontAwesomeIcon
          icon={expandedSections[sectionKey] ? faChevronUp : faChevronDown}
          className="text-gray-400"
        />
      </button>
      {expandedSections[sectionKey] && (
        <div className="px-4 py-3 bg-white max-h-64 overflow-y-auto">
          {children}
        </div>
      )}
    </div>
  );

  const RecordItem = ({ record, showChanges = false }) => (
    <div className="border-b border-gray-100 last:border-b-0 py-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-gray-900">PO: {record.purchaseOrder}</span>
          <span className="ml-2 text-sm text-gray-500">Row {record.row}</span>
        </div>
        {showChanges && record.changedFields && (
          <div className="text-sm text-blue-600">
            Changed: {record.changedFields.join(', ')}
          </div>
        )}
      </div>
      {record.reason && (
        <p className="text-sm text-gray-600 mt-1">{record.reason}</p>
      )}
      {record.error && (
        <p className="text-sm text-red-600 mt-1">Error: {record.error}</p>
      )}
    </div>
  );

  const ValidationIssueItem = ({ issue }) => (
    <div className="border-b border-gray-100 last:border-b-0 py-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-gray-900">Row {issue.row}</span>
          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
            issue.severity === 'high' ? 'bg-red-100 text-red-800' :
            issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {issue.severity}
          </span>
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-1">{issue.issue}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faFileUpload} className="text-blue-600 text-xl mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Upload Results</h2>
              <p className="text-sm text-gray-600">{uploadResult.fileInfo?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={faCheckCircle}
              title="New Records"
              value={uploadResult.summary?.newRecords || 0}
              color="green"
              subtitle="Added to database"
            />
            <StatCard
              icon={faSync}
              title="Updated Records"
              value={uploadResult.summary?.updatedRecords || 0}
              color="blue"
              subtitle="Existing records modified"
            />
            <StatCard
              icon={faExclamationTriangle}
              title="Skipped Records"
              value={uploadResult.summary?.skippedRecords || 0}
              color="yellow"
              subtitle="No changes or missing data"
            />
            <StatCard
              icon={faTimesCircle}
              title="Failed Records"
              value={uploadResult.summary?.failedRecords || 0}
              color="red"
              subtitle="Processing errors"
            />
          </div>

          {/* File and Processing Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* File Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <FontAwesomeIcon icon={faFileExcel} className="text-green-600 mr-2" />
                File Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">File Name:</span>
                  <span className="font-medium">{uploadResult.fileInfo?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">File Type:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    uploadResult.fileInfo?.type === 'MP' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                  }`}>
                    {uploadResult.fileInfo?.type} ({uploadResult.fileInfo?.type === 'MP' ? 'Materia Prima' : 'Producto Terminado'})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">File Size:</span>
                  <span className="font-medium">{uploadResult.fileInfo?.sizeFormatted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Rows:</span>
                  <span className="font-medium">{uploadResult.summary?.totalProcessed}</span>
                </div>
              </div>
            </div>

            {/* Processing Statistics */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <FontAwesomeIcon icon={faCog} className="text-gray-600 mr-2" />
                Processing Statistics
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Processing Time:</span>
                  <span className="font-medium">{uploadResult.timing?.processingTimeFormatted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Records/Second:</span>
                  <span className="font-medium">{uploadResult.timing?.recordsPerSecond}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Success Rate:</span>
                  <span className={`font-medium ${getSuccessRateColor()}`}>
                    {getSuccessRate()}%
                  </span>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Overall Progress</span>
                    <span>{getSuccessRate()}%</span>
                  </div>
                  <ProgressBar percentage={getSuccessRate()} color={getSuccessRate() >= 90 ? 'green' : getSuccessRate() >= 70 ? 'yellow' : 'red'} />
                </div>
              </div>
            </div>
          </div>

          {/* Data Quality Report */}
          {uploadResult.dataQuality && (
            <ExpandableSection
              title="Data Quality Report"
              count={`${Math.round(uploadResult.dataQuality.averageCompleteness)}% avg completeness`}
              icon={faChartPie}
              color="purple"
              sectionKey="dataQuality"
            >
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Average Completeness</p>
                    <p className="text-xl font-bold text-purple-600">
                      {Math.round(uploadResult.dataQuality.averageCompleteness)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Missing Fields</p>
                    <p className="text-xl font-bold text-yellow-600">
                      {uploadResult.dataQuality.totalMissingFields}
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Data Completeness</span>
                    <span>{Math.round(uploadResult.dataQuality.averageCompleteness)}%</span>
                  </div>
                  <ProgressBar
                    percentage={uploadResult.dataQuality.averageCompleteness}
                    color="purple"
                  />
                </div>
              </div>
            </ExpandableSection>
          )}

          {/* Detailed Sections */}
          {uploadResult.statistics?.insertedRecords?.length > 0 && (
            <ExpandableSection
              title="New Records Added"
              count={uploadResult.statistics.insertedRecords.length}
              icon={faCheckCircle}
              color="green"
              sectionKey="insertedRecords"
            >
              <div className="space-y-1">
                {uploadResult.statistics.insertedRecords.map((record, index) => (
                  <RecordItem key={index} record={record} />
                ))}
              </div>
            </ExpandableSection>
          )}

          {uploadResult.statistics?.updatedRecords?.length > 0 && (
            <ExpandableSection
              title="Updated Records"
              count={uploadResult.statistics.updatedRecords.length}
              icon={faSync}
              color="blue"
              sectionKey="updatedRecords"
            >
              <div className="space-y-1">
                {uploadResult.statistics.updatedRecords.map((record, index) => (
                  <RecordItem key={index} record={record} showChanges={true} />
                ))}
              </div>
            </ExpandableSection>
          )}

          {uploadResult.statistics?.skippedRecords?.length > 0 && (
            <ExpandableSection
              title="Skipped Records"
              count={uploadResult.statistics.skippedRecords.length}
              icon={faExclamationTriangle}
              color="yellow"
              sectionKey="skippedRecords"
            >
              <div className="space-y-1">
                {uploadResult.statistics.skippedRecords.map((record, index) => (
                  <RecordItem key={index} record={record} />
                ))}
              </div>
            </ExpandableSection>
          )}

          {uploadResult.statistics?.failedRecords?.length > 0 && (
            <ExpandableSection
              title="Failed Records"
              count={uploadResult.statistics.failedRecords.length}
              icon={faTimesCircle}
              color="red"
              sectionKey="failedRecords"
            >
              <div className="space-y-1">
                {uploadResult.statistics.failedRecords.map((record, index) => (
                  <RecordItem key={index} record={record} />
                ))}
              </div>
            </ExpandableSection>
          )}

          {uploadResult.statistics?.validationIssues?.length > 0 && (
            <ExpandableSection
              title="Validation Issues"
              count={uploadResult.statistics.validationIssues.length}
              icon={faInfoCircle}
              color="orange"
              sectionKey="validationIssues"
            >
              <div className="space-y-1">
                {uploadResult.statistics.validationIssues.map((issue, index) => (
                  <ValidationIssueItem key={index} issue={issue} />
                ))}
              </div>
            </ExpandableSection>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Upload completed at {new Date(uploadResult.timestamp).toLocaleString()}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                // Export functionality could be implemented here
                console.log('Export report:', uploadResult);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <FontAwesomeIcon icon={faDownload} />
              <span>Export Report</span>
            </button>
            <button
              onClick={onClose}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
            >
              <span>Close</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadResultsModal;